import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import Header from '@/components/layout/Header';
import MobileNav from '@/components/layout/MobileNav';
import PostCard from '@/components/feed/PostCard';
import CreatePostDialog from '@/components/feed/CreatePostDialog';
import { Button } from '@/components/ui/button';
import { Plus, RefreshCw, UserPlus, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function FeedPage() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [familyMemberCount, setFamilyMemberCount] = useState(null);

  const fetchPosts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/posts`, { withCredentials: true });
      setPosts(response.data);
    } catch (error) {
      console.error('Failed to fetch posts:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchFamilyMemberCount = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/family`, { withCredentials: true });
      setFamilyMemberCount(response.data?.members?.length ?? null);
    } catch (error) {
      /* non-fatal */
    }
  }, []);

  useEffect(() => {
    fetchPosts();
    fetchFamilyMemberCount();
  }, [fetchPosts, fetchFamilyMemberCount]);

  const handlePostCreated = (newPost) => {
    setPosts(prev => [newPost, ...prev]);
    toast.success('Post shared with your family!');
  };

  const handlePostDeleted = (postId) => {
    setPosts(prev => prev.filter(p => p.id !== postId));
    toast.success('Post deleted');
  };

  const handleReactionUpdated = async (postId) => {
    // Refresh the specific post
    try {
      const response = await axios.get(`${API}/posts/${postId}`, { withCredentials: true });
      setPosts(prev => prev.map(p => p.id === postId ? response.data : p));
    } catch (error) {
      console.error('Failed to refresh post:', error);
    }
  };

  const canPost = user?.role === 'admin' || user?.role === 'member';

  return (
    <div className="min-h-screen bg-[#FDFBF7] pb-20 md:pb-8" data-testid="feed-page">
      <Header />
      
      <main className="max-w-2xl mx-auto px-4 py-6">
        {/* Create Post Button - Desktop */}
        <div className="hidden md:flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-stone-900">Family Feed</h1>
          {canPost && (
            <Button
              onClick={() => setCreateDialogOpen(true)}
              className="bg-teal-600 hover:bg-teal-700 text-white rounded-full"
              data-testid="create-post-btn"
            >
              <Plus className="w-5 h-5 mr-2" />
              Share Update
            </Button>
          )}
        </div>

        {/* Solo-family invite CTA (only when you're alone in your family) */}
        {!loading && familyMemberCount === 1 && user?.role === 'admin' && (
          <div
            className="mb-6 rounded-3xl bg-gradient-to-br from-teal-600 to-teal-700 p-6 md:p-8 text-white shadow-xl relative overflow-hidden"
            data-testid="solo-invite-cta"
          >
            <div className="absolute -right-6 -top-6 w-32 h-32 bg-white/10 rounded-full" />
            <div className="relative">
              <span className="inline-flex items-center gap-1 text-xs font-medium bg-white/20 backdrop-blur rounded-full px-3 py-1 mb-3">
                <Sparkles className="w-3 h-3" /> You're the only one here
              </span>
              <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-2">
                Bring your family in
              </h2>
              <p className="text-teal-50 mb-5 max-w-md">
                Kinsy is only magical when your people are here too. Invite your first family member — grandparents, siblings, or your partner — and start sharing.
              </p>
              <Button asChild className="bg-white text-teal-700 hover:bg-stone-50 rounded-full font-semibold" data-testid="solo-invite-btn">
                <Link to="/family">
                  <UserPlus className="w-4 h-4 mr-2" /> Send your first invite
                </Link>
              </Button>
            </div>
          </div>
        )}

        {/* Posts */}
        <div className="space-y-6">
          {loading ? (
            <div className="text-center py-12">
              <RefreshCw className="w-8 h-8 text-teal-600 animate-spin mx-auto mb-4" />
              <p className="text-stone-500">Loading posts...</p>
            </div>
          ) : posts.length === 0 ? (
            <div className="card-soft text-center py-12" data-testid="empty-feed">
              <div className="w-16 h-16 bg-teal-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <Plus className="w-8 h-8 text-teal-600" />
              </div>
              <h2 className="text-xl font-semibold text-stone-900 mb-2">Your family story starts here</h2>
              <p className="text-stone-500 mb-6">
                {canPost 
                  ? 'Share a photo, milestone, or everyday moment. The people who love you are waiting.' 
                  : 'Family moments will appear here as loved ones share them.'}
              </p>
              {canPost && (
                <Button
                  onClick={() => setCreateDialogOpen(true)}
                  className="bg-teal-600 hover:bg-teal-700 text-white rounded-full"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Share your first moment
                </Button>
              )}
            </div>
          ) : (
            posts.map(post => (
              <PostCard
                key={post.id}
                post={post}
                currentUser={user}
                onDeleted={handlePostDeleted}
                onReactionUpdated={handleReactionUpdated}
                onBlocked={() => fetchPosts()}
              />
            ))
          )}
        </div>
      </main>

      <MobileNav onCreatePost={canPost ? () => setCreateDialogOpen(true) : null} />
      
      {canPost && (
        <CreatePostDialog
          open={createDialogOpen}
          onOpenChange={setCreateDialogOpen}
          onPostCreated={handlePostCreated}
        />
      )}
    </div>
  );
}
